import React, { useState, useCallback, useEffect, useContext, useMemo } from 'react';
import { Input, mergeClasses, Tooltip } from '@fluentui/react-components';
import { I18nContext, I18nModel } from '@bingads-webui/copilot-i18n';
import { ConversationMoreOptionsMenu } from './conversation-more-options-menu';
import { ConversationDeleteDialog } from './conversation-delete-dialog';
import { useConversationItemStyles } from './styles';
import type { ConversationItemProps } from './types';

export const ConversationItem = (props: ConversationItemProps) => {
  const styles = useConversationItemStyles();
  const {
    title,
    conversationId,
    onClick,
    onDelete,
    onRename,
    isActive = false,
    position,
    conversationsLength,
    timePeriod,
  } = props;

  const [isInEditMode, setIsInEditMode] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [inputValue, setInputValue] = useState(title);
  const [isHovered, setIsHovered] = useState(false);
  const [hasError, setHasError] = useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const i18n = useContext(I18nContext) as I18nModel;

  // Strip JSON entity objects from title to display only entity names
  const displayTitle = useMemo(
    () => (title || '')
      .replace(/\{"entityType":"[^"]+","entityId":"[^"]+","entityName":"([^"]+)"\}/g, (_, name) => name)
      .replace(/{(Campaign|AdGroup):([^}]+)}/g, (_, _type, name) => name),
    [title]
  );


  const handleInputChange = useCallback((_e: React.ChangeEvent<HTMLInputElement>, data: { value: string }) => {
    setInputValue(data.value);
  }, []);

  const resetInput = useCallback(() => {
    setIsInEditMode(false);
    setInputValue(title);
  }, [title]);

  const handleRenameSubmit = useCallback(() => {
    if (!inputValue.trim()) {
      setHasError(true);
      return;
    }

    if (inputValue.trim()) {
      onRename(conversationId, inputValue.trim());
    }
    resetInput();
  }, [conversationId, inputValue, onRename, resetInput]);

  const handleRenameClick = useCallback(() => {
    setIsInEditMode(true);
    setInputValue(title);
  }, [title]);

  const handleInputBlur = useCallback(() => {
    handleRenameSubmit();
    setHasError(false);
    resetInput();
  }, [handleRenameSubmit, resetInput]);

  /* istanbul ignore next */
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleRenameSubmit();
    } else if (e.key === 'Escape') {
      resetInput();
    } else {
      setHasError(false);
    }
  }, [handleRenameSubmit, resetInput]);

  /* istanbul ignore next */
  const handleItemKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick(conversationId);
    }
  }, [onClick, conversationId]);

  useEffect(() => {
    if (isInEditMode) {
      inputRef.current?.focus();
    }
  }, [isInEditMode]);

  return (
    <>
      {!isInEditMode ? (
        <div
          className={mergeClasses(styles.wrapper, isHovered && styles.wrapperHovered, isActive && styles.activeItem)}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onFocus={() => setIsHovered(true)}
          onBlur={() => setIsHovered(false)}
        >
          <div className={styles.listItemWrapper}>
            <div
              className={mergeClasses('conversation-item', styles.listItem, isHovered && styles.listItemHovered)}
              onClick={() => onClick(conversationId)}
              onKeyDown={handleItemKeyDown}
              tabIndex={0}
              role="button"
              aria-label={`Conversation: ${displayTitle} ${position ? ` ${position} of ${conversationsLength} from ${timePeriod}` : ''}`}
            >
              <Tooltip content={displayTitle || i18n.getString(_TL_('Untitled conversation'))} relationship="description">
                <span className={mergeClasses(styles.textContent, isHovered && styles.textContentHovered)}>
                  {displayTitle || i18n.getString(_TL_('Untitled conversation'))}
                </span>
              </Tooltip>
            </div>
          </div>

          <div
            className={mergeClasses(
              styles.moreOptionsContainer,
              isHovered && styles.visible
            )}
          >
            <ConversationMoreOptionsMenu
              onRenameClick={handleRenameClick}
              onDeleteClick={() => { setDeleteDialogOpen(true); }}
            />
          </div>
        </div>
      ) :
        <>
          <Input
            id={conversationId}
            value={inputValue}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            className={mergeClasses(styles.input, 'rename-input', hasError && styles.inputError)}
            onKeyDown={handleKeyDown}
            ref={inputRef}
          />
          {hasError && (
            <div className={mergeClasses('conversation-item-inline-error', styles.inlineError)}>
              {i18n.getString(_TL_('Invalid name. Please update it.'))}
            </div>
          )}
        </>
      }
      <ConversationDeleteDialog
        open={deleteDialogOpen}
        setOpen={setDeleteDialogOpen}
        onConfirm={() => onDelete(conversationId)}
        conversationTitle={title}
      />
    </>
  );
};
